import type { UserPreferencesData } from '@features/user-preferences/types';
import type { AnyBlockNoteEditor } from '@utils/blocknote/types';
import { marked } from 'marked';
import removeMd from 'remove-markdown';

import {
  buildSelectionCopied,
  type TelemetryContext,
} from '../../../utils/analytics/noteTelemetryBuilder';
import { escapeHtml } from '../../../utils/htmlEntities';
import { copyRichText } from '../../../utils/utils';

import { parseMarkdownH2Sections } from './markdown-utils';

interface CopySectionsOptions {
  jotdocContent: string;
  jotdocTitle?: string;
  selectedSectionIds: string[];
  copyAll: boolean;
  blockNoteEditors: Map<string, AnyBlockNoteEditor>;
  userPreferences: Pick<UserPreferencesData, 'copy_paste_preferences'> | null | undefined;
  jotdocId?: string;
  encounterId?: string;
  getDocVersion?: () => string;
}

interface CopySectionsResult {
  success: boolean;
  message: string;
}

/**
 * Copy sections from a jotdoc to clipboard
 * Supports copying all sections or only selected sections
 * Uses BlockNote editors when available for better formatting
 */
export async function copyJotDocSections(
  options: CopySectionsOptions,
): Promise<CopySectionsResult> {
  const {
    jotdocContent,
    jotdocTitle,
    selectedSectionIds,
    copyAll,
    blockNoteEditors,
    userPreferences,
    jotdocId,
    encounterId,
    getDocVersion,
  } = options;

  let htmlString = '';
  let plainTextString = '';

  // Get user preferences for copying
  const prefs = userPreferences;
  const includeHeaderOnCopyMultiple =
    prefs?.copy_paste_preferences?.include_header_on_copy_multiple ?? true;
  const includeHtml = prefs?.copy_paste_preferences?.html_formatting_on_copy ?? true;

  // Parse sections from markdown
  const allSections = parseMarkdownH2Sections(jotdocContent);
  const totalSections = allSections.length;

  // Check if there are any sections
  if (totalSections === 0) {
    return {
      success: false,
      message: 'No sections available to copy',
    };
  }

  // Check if user is trying to copy selected sections but hasn't selected any
  if (!copyAll && selectedSectionIds.length === 0) {
    return {
      success: false,
      message: 'No sections selected. Please select sections to copy.',
    };
  }

  // Add header if preference is enabled and copying multiple sections
  const copyingMultiple = copyAll || selectedSectionIds.length > 1;
  if (copyingMultiple && includeHeaderOnCopyMultiple && jotdocTitle) {
    const headerPlainText = `Note Title: ${jotdocTitle}\n\n`;
    const headerHtml = `<p><strong>Note Title: ${jotdocTitle}</strong></p><br/>`;
    plainTextString += headerPlainText;
    htmlString += headerHtml;
  }

  // Iterate over all sections
  for (const section of allSections) {
    // Check if the section should be copied
    if (!copyAll && !selectedSectionIds.includes(section.id)) {
      continue;
    }

    // Clean section name by removing text between parentheses
    const cleanedName = section.title.replace(/\s*\([^)]*\)/g, '');
    const sectionTitle = cleanedName;

    // Add section header to both strings
    plainTextString += `===\n${sectionTitle}\n===\n\n`;
    htmlString +=
      htmlString.length > 0
        ? `<hr/><h2>${escapeHtml(sectionTitle)}</h2>`
        : `<h2>${escapeHtml(sectionTitle)}</h2>`;

    // Get BlockNote editor if available
    const blockNoteEditor = blockNoteEditors.get(section.id);

    if (blockNoteEditor) {
      // Use BlockNote's native HTML conversion
      try {
        const sectionHtml = await blockNoteEditor.blocksToHTMLLossy(blockNoteEditor.document);
        const sectionMarkdown = await blockNoteEditor.blocksToMarkdownLossy(
          blockNoteEditor.document,
        );

        htmlString += sectionHtml;
        plainTextString += sectionMarkdown + '\n\n';
      } catch (e) {
        console.error('Error converting BlockNote content:', e);
        // Fallback to marked if BlockNote conversion fails
        try {
          const sectionHtml = await marked(section.text);
          htmlString += sectionHtml;
          plainTextString +=
            removeMd(section.text, {
              stripListLeaders: false,
              useImgAltText: false,
            }) + '\n\n';
        } catch (markdownErr) {
          console.error('Error parsing markdown:', markdownErr);
          htmlString += `<div style="white-space: pre-wrap;">${section.text}</div>`;
          plainTextString += section.text + '\n\n';
        }
      }
    } else {
      // Fallback: Use marked if no BlockNote editor is available
      try {
        const sectionHtml = await marked(section.text);
        htmlString += sectionHtml;
        plainTextString +=
          removeMd(section.text, {
            stripListLeaders: false,
            useImgAltText: false,
          }) + '\n\n';
      } catch (e) {
        console.error('Error parsing markdown:', e);
        // Fallback: treat as plain text if markdown parsing fails
        htmlString += `<div style="white-space: pre-wrap;">${section.text}</div>`;
        plainTextString += section.text + '\n\n';
      }
    }

    htmlString += '<br/>'; // Add a break after each section's content in HTML
  }

  // Use copyRichText utility
  const success = await copyRichText(htmlString, plainTextString, includeHtml);

  if (success) {
    // Create action-specific success messages
    let successMessage = '';
    if (copyAll) {
      successMessage = `All ${totalSections} section${totalSections === 1 ? '' : 's'} copied to clipboard!`;
    } else {
      const copiedCount = selectedSectionIds.length;
      successMessage = `${copiedCount} selected section${copiedCount === 1 ? '' : 's'} copied to clipboard!`;
    }

    // Track telemetry events for each copied section
    if (jotdocId) {
      const context: TelemetryContext = {
        docId: `jotdoc:${jotdocId}`,
        getDocVersion: getDocVersion || (() => '0'),
      };

      // Determine which sections were copied
      const copiedSections = copyAll
        ? allSections
        : allSections.filter(section => selectedSectionIds.includes(section.id));

      // Track one event per section
      for (const section of copiedSections) {
        // Get section text length from BlockNote editor if available, otherwise use parsed markdown
        const blockNoteEditor = blockNoteEditors.get(section.id);
        let sectionTextLength = section.text.length;

        if (blockNoteEditor) {
          try {
            const sectionMarkdown = await blockNoteEditor.blocksToMarkdownLossy(
              blockNoteEditor.document,
            );
            sectionTextLength = sectionMarkdown.length;
          } catch (e) {
            // Fallback to parsed markdown length if BlockNote conversion fails
            sectionTextLength = section.text.length;
          }
        }

        buildSelectionCopied({
          context,
          lenSelected: sectionTextLength,
          lenTotal: sectionTextLength,
          modality: copyAll ? 'copy_all' : 'copy_selected',
          metadata: {
            note_id: jotdocId,
            section_id: section.id,
            section_title: section.title,
            encounter_id: encounterId,
          },
        });
      }
    }

    return {
      success: true,
      message: successMessage,
    };
  } else {
    return {
      success: false,
      message: 'Failed to copy sections.',
    };
  }
}
